import { Button, ControlGroup, FormGroup, HTMLSelect, Icon, Intent, NonIdealState } from "@blueprintjs/core";
import classNames from "classnames";
import { JitsiTrackEffect } from "lib/lib-jitsi-meet";
import { action, observable, reaction, runInAction } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import ResizeDetector from "react-resize-detector";

import { Configuration, LocalUserState, MediaDevicesState, RoomState } from "../../common";
import { getVirtualBackgroundEffect, IVirtualBackgroundEffect } from "../../video";
import { JitsiManagerChildProps } from "../data/JitsiManager";
import AudioMeter from "./AudioMeter";
import MediaDevices from "./MediaDevices";
import NicknameInput from "./NicknameInput";
import VideoEffectsDropdown from "./VideoEffectsDropdown";

import MicrophoneIcon from "../../assets/icons/microphone.inline.svg";
import styles from "./SelfView.module.css";

export interface SelfViewProps extends JitsiManagerChildProps {
    roomState: RoomState;
    mediaDevices: MediaDevicesState;
    localUser: LocalUserState;

    // HACKHACK... these are weird options... should be refactored into another component
    responsiveWidth?: boolean;
    showOptions?: boolean;
}

@observer
export default class SelfView extends React.PureComponent<SelfViewProps, never> {
    public readonly displayName = "SelfView";
    public static defaultProps: Partial<SelfViewProps> = {
        responsiveWidth: true,
        showOptions: true,
    };

    @observable public videoState: {
        virtualBackgroundEffect?: IVirtualBackgroundEffect;
        jitsiBlurEffect?: JitsiTrackEffect;
    } = {};

    private selfVideoEl?: HTMLVideoElement;
    private handleVideoDataLoaded = () => (this.props.localUser.isSelfVideoDataLoaded = true);
    private handleSelfVideoRef = (el: HTMLVideoElement) => {
        if (el != null) {
            this.selfVideoEl = el;
            this.selfVideoEl.addEventListener("loadeddata", this.handleVideoDataLoaded);
        }
    };

    private selfVideoCanvas?: HTMLCanvasElement | null;
    private handleSelfVideoCanvasRef = (el: HTMLCanvasElement) => (this.selfVideoCanvas = el);

    @action
    private attachStreamToVideoEl = async () => {
        if (typeof window === "undefined") {
            return;
        }

        const { mediaDevices, localUser } = this.props;
        // video source should be the original media stream, not the one with our FX processing
        const stream = mediaDevices.videoInputTrack?.stream.clone();

        if (this.selfVideoEl != null && this.selfVideoCanvas != null && stream !== undefined) {
            this.selfVideoEl.srcObject = stream;

            this.videoState.virtualBackgroundEffect = await getVirtualBackgroundEffect(
                this.selfVideoEl,
                this.selfVideoCanvas,
                localUser
            );

            if (localUser.videoEffect === "virtualBackground") {
                mediaDevices.videoInputTrack!.setEffect(this.videoState.virtualBackgroundEffect);
            }
        }
    };

    public async componentDidMount() {
        const { mediaDevices, localUser } = this.props;

        if (mediaDevices.videoInputTrack != null) {
            this.attachStreamToVideoEl();
        }

        reaction(() => mediaDevices.videoInputTrack, this.attachStreamToVideoEl);
        reaction(
            () => localUser.videoEffect,
            (effect) => {
                if (effect === "virtualBackground") {
                    mediaDevices.videoInputTrack!.setEffect(this.videoState.virtualBackgroundEffect);
                } else if (effect === "jitsiBlur" && this.videoState.jitsiBlurEffect !== undefined) {
                    mediaDevices.videoInputTrack!.setEffect(this.videoState.jitsiBlurEffect);
                } else {
                    mediaDevices.videoInputTrack!.setEffect(undefined);
                }
            }
        );
    }

    public componentWillUnmount() {
        this.selfVideoEl?.removeEventListener("loadeddata", this.handleVideoDataLoaded);
    }

    public render() {
        if (this.props.mediaDevices.videoInputs.length > 0) {
            return (
                <div
                    className={classNames(styles.selfViewContainer, {
                        [styles.fillWidth]: !this.props.responsiveWidth,
                    })}
                >
                    {this.renderViewContents()}
                </div>
            );
        } else {
            return <NonIdealState title="Your webcam is unavailable :(" icon="mobile-video" />;
        }
    }

    private renderViewContents() {
        const { room, roomState, localUser, mediaDevices, showOptions, responsiveWidth } = this.props;
        const isSelfVideoEffectStarted =
            ((localUser.videoEffect === "jitsiBlur" &&
                this.videoState.jitsiBlurEffect?.isEnabled(mediaDevices.videoInputTrack!)) ||
                this.videoState.virtualBackgroundEffect?.isStarted) ??
            false;
        const showVideoEffectsDropdown = Configuration.ENABLE_SELF_VIDEO_EFFECTS && roomState.roomJoined === "joined";

        return (
            <>
                {showOptions && (
                    <div className={styles.selfInfoContainer}>
                        <NicknameInput room={room} localUser={localUser} />
                        {showVideoEffectsDropdown && this.videoState.virtualBackgroundEffect !== undefined ? (
                            <VideoEffectsDropdown
                                localUser={localUser}
                                effect={this.videoState.virtualBackgroundEffect}
                            />
                        ) : undefined}
                    </div>
                )}
                <div
                    className={classNames(styles.selfVideoContainer, {
                        [styles.withVideoEffect]: isSelfVideoEffectStarted,
                    })}
                >
                    {responsiveWidth ? (
                        <ResizeDetector handleWidth={true} refreshMode="throttle">
                            {this.renderSelfImage}
                        </ResizeDetector>
                    ) : (
                        this.renderSelfImage()
                    )}
                    {showOptions && this.renderLocalVideoActions()}
                </div>
                {showOptions && (
                    <>
                        {this.maybeRenderAudioMeter()}
                        {this.renderMediaDevices()}
                    </>
                )}
            </>
        );
    }

    private renderSelfImage = (dim?: { width: number }) => {
        const dimensions: { width?: number | string; height?: number | string } = {};
        if (dim === undefined) {
            dimensions.width = "100%";
        } else if (dim.width > 0) {
            dimensions.width = dim.width;
            dimensions.height = dim.width / Configuration.VIDEO_ASPECT_RATIO_LANDSCAPE;
        }

        return (
            <>
                <canvas className={styles.selfVideoCanvas} ref={this.handleSelfVideoCanvasRef} {...dimensions} />
                <video
                    className={styles.selfVideo}
                    autoPlay={true}
                    ref={this.handleSelfVideoRef}
                    playsInline={true}
                    muted={true}
                    {...dimensions}
                />
            </>
        );
    };

    private renderLocalVideoActions() {
        const { roomJoined } = this.props.roomState;
        return (
            <div className={styles.selfVideoActions}>
                <ControlGroup>
                    {roomJoined === "joined" && (
                        <Button text="Leave the party" small={true} intent={Intent.DANGER} onClick={this.leaveRoom} />
                    )}
                </ControlGroup>
            </div>
        );
    }

    private maybeRenderAudioMeter() {
        const { isHost, mediaDevices, localUser } = this.props;

        if (isHost && mediaDevices.audioInputTrack !== undefined) {
            return <AudioMeter meterState={localUser.audioInputMeter} stream={mediaDevices.audioInputTrack.stream} />;
        }
    }

    private renderMediaDevices() {
        const { isHost, mediaDevices } = this.props;
        const { audioInputs, videoInputs, audioInputDeviceId, videoInputDeviceId } = mediaDevices;

        return (
            <MediaDevices>
                {isHost ? (
                    <FormGroup
                        className={styles.audioInputFormGroup}
                        label={<MicrophoneIcon />}
                        labelFor="audio-input-select"
                        inline={true}
                    >
                        <HTMLSelect
                            id="audio-input-select"
                            minimal={true}
                            value={audioInputDeviceId}
                            options={audioInputs.map(({ deviceId, label }) => ({
                                label,
                                value: deviceId,
                            }))}
                            onChange={({ target }) => (mediaDevices.audioInputDeviceId = target.value)}
                        />
                    </FormGroup>
                ) : undefined}
                <FormGroup label={<Icon icon="mobile-video" />} labelFor="video-input-select" inline={true}>
                    <HTMLSelect
                        id="video-input-select"
                        minimal={true}
                        value={videoInputDeviceId}
                        options={videoInputs.map(({ deviceId, label }) => ({
                            label,
                            value: deviceId,
                        }))}
                        onChange={({ target }) => (mediaDevices.videoInputDeviceId = target.value)}
                    />
                </FormGroup>
            </MediaDevices>
        );
    }

    private leaveRoom = async () => {
        const { roomState, onLeaveRoom } = this.props;
        await onLeaveRoom();
        runInAction(() => {
            roomState.roomJoined = "left";
        });
    };
}
